/**
 * Register Page - Split layout with hero image
 * Features: Confirm password, password strength, caps lock warning
 */
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Loader2, Link2, Eye, EyeOff, Check, X, AlertTriangle } from 'lucide-react';

// Password strength rules
const PASSWORD_RULES = [
  { id: 'length', label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { id: 'uppercase', label: 'One uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { id: 'lowercase', label: 'One lowercase letter', test: (p) => /[a-z]/.test(p) },
  { id: 'number', label: 'One number', test: (p) => /[0-9]/.test(p) },
  { id: 'special', label: 'One special character (!@#$%^&*)', test: (p) => /[!@#$%^&*(),.?":{}|<>]/.test(p) },
];

const Register = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  // Check caps lock state
  const handleKeyDown = (e) => {
    if (e.getModifierState) {
      setCapsLockOn(e.getModifierState('CapsLock'));
    }
  };

  // Calculate password strength
  const getPasswordStrength = () => {
    const passed = PASSWORD_RULES.filter(rule => rule.test(password)).length;
    if (passed === 0) return { score: 0, label: '', color: '' };
    if (passed <= 2) return { score: 1, label: 'Weak', color: 'bg-red-500' };
    if (passed <= 3) return { score: 2, label: 'Fair', color: 'bg-yellow-500' };
    if (passed <= 4) return { score: 3, label: 'Good', color: 'bg-blue-500' };
    return { score: 4, label: 'Strong', color: 'bg-emerald-500' };
  };

  const strength = getPasswordStrength();
  const passwordsMatch = password && confirmPassword && password === confirmPassword;
  const passwordsDontMatch = confirmPassword && password !== confirmPassword;
  const allRulesPassed = PASSWORD_RULES.every(rule => rule.test(password));

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name || !name.trim()) {
      toast.error('Please enter your full name');
      return;
    }

    if (!email && !phone) {
      toast.error('Please provide either email or phone number');
      return;
    }

    if (!password) {
      toast.error('Please choose a password');
      return;
    }

    if (!allRulesPassed) {
      const missing = PASSWORD_RULES.filter(r => !r.test(password)).map(r => r.label);
      toast.error(`Password missing: ${missing.join(', ')}`);
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setIsLoading(true);

    try {
      await register({
        name,
        email: email || null,
        phone: phone || null,
        password,
      });
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || error.response?.data?.error?.message || 'Registration failed';
      
      // Handle specific error codes
      if (message.includes('ACCOUNT_DEACTIVATED')) {
        toast.error('This account has been deactivated. Please contact support.');
      } else {
        toast.error(message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen w-full overflow-hidden bg-slate-950">
      {/* Left Side - Form */}
      <div className="flex flex-col justify-center px-8 md:px-16 lg:px-20 py-12 overflow-y-auto">
        <div className="max-w-md w-full mx-auto">
          {/* Logo */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <Link2 className="h-8 w-8 text-amber-500" />
              <span className="text-2xl font-bold text-white tracking-tight font-['Manrope']">
                Liantiao
              </span>
            </div>
            <p className="text-slate-400 text-sm font-['Manrope']">The Chain - Jamaica's Logistics Network</p>
          </div>

          {/* Header */}
          <div className="mb-6">
            <h1 className="text-3xl md:text-4xl font-bold text-white tracking-tight font-['Manrope'] mb-2">
              Create account
            </h1>
            <p className="text-slate-400 font-['Manrope']">
              Join the logistics network
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-slate-300 font-['Manrope']">
                Full Name
              </Label>
              <Input
                id="name"
                data-testid="register-name-input"
                type="text"
                placeholder="John Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="bg-slate-950 border-slate-800 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 rounded-sm h-11 text-white placeholder:text-slate-600"
                required
                minLength={2}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-300 font-['Manrope']">
                Email <span className="text-slate-500">(optional if phone provided)</span>
              </Label>
              <Input
                id="email"
                data-testid="register-email-input"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-slate-950 border-slate-800 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 rounded-sm h-11 text-white placeholder:text-slate-600"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-slate-300 font-['Manrope']">
                Phone <span className="text-slate-500">(optional if email provided)</span>
              </Label>
              <Input
                id="phone"
                data-testid="register-phone-input"
                type="tel"
                placeholder="18761234567"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="bg-slate-950 border-slate-800 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 rounded-sm h-11 text-white placeholder:text-slate-600"
              />
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-300 font-['Manrope']">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  data-testid="register-password-input"
                  type={showPassword ? "text" : "password"}
                  placeholder="Create a strong password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setPasswordFocused(true)}
                  onBlur={() => setPasswordFocused(false)}
                  onKeyDown={handleKeyDown}
                  onKeyUp={handleKeyDown}
                  className="bg-slate-950 border-slate-800 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 rounded-sm h-11 text-white placeholder:text-slate-600 pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>

              {/* Caps Lock Warning */}
              {capsLockOn && passwordFocused && (
                <div className="flex items-center gap-2 text-yellow-500 text-sm mt-1">
                  <AlertTriangle className="h-4 w-4" />
                  <span>Caps Lock is on</span>
                </div>
              )}

              {/* Password Strength Indicator */}
              {password && (
                <div className="mt-2 space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all duration-300 ${strength.color}`}
                        style={{ width: `${(strength.score / 4) * 100}%` }}
                      />
                    </div>
                    {strength.label && (
                      <span className={`text-xs font-medium ${
                        strength.score <= 1 ? 'text-red-400' :
                        strength.score === 2 ? 'text-yellow-400' :
                        strength.score === 3 ? 'text-blue-400' :
                        'text-emerald-400'
                      }`}>
                        {strength.label}
                      </span>
                    )}
                  </div>
                  
                  {/* Password Rules Checklist */}
                  <div className="grid grid-cols-2 gap-1">
                    {PASSWORD_RULES.map(rule => (
                      <div 
                        key={rule.id}
                        className={`flex items-center gap-1.5 text-xs ${
                          rule.test(password) ? 'text-emerald-400' : 'text-slate-500'
                        }`}
                      >
                        {rule.test(password) ? (
                          <Check className="h-3 w-3" />
                        ) : (
                          <X className="h-3 w-3" />
                        )}
                        <span>{rule.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Confirm Password Field */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-slate-300 font-['Manrope']">
                Confirm Password
              </Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  data-testid="register-confirm-password-input"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Re-enter your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onKeyUp={handleKeyDown}
                  className={`bg-slate-950 border-slate-800 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 rounded-sm h-11 text-white placeholder:text-slate-600 pr-10 ${
                    passwordsMatch ? 'border-emerald-500' : passwordsDontMatch ? 'border-red-500' : ''
                  }`}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                >
                  {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              
              {/* Password Match Indicator */}
              {confirmPassword && (
                <div className={`flex items-center gap-2 text-sm ${
                  passwordsMatch ? 'text-emerald-400' : 'text-red-400'
                }`}>
                  {passwordsMatch ? (
                    <>
                      <Check className="h-4 w-4" />
                      <span>Passwords match</span>
                    </>
                  ) : (
                    <>
                      <X className="h-4 w-4" />
                      <span>Passwords do not match</span>
                    </>
                  )}
                </div>
              )}
            </div>

            <Button
              type="submit"
              data-testid="register-submit-btn"
              disabled={isLoading}
              className="w-full bg-amber-500 text-slate-950 hover:bg-amber-400 rounded-sm h-11 font-medium tracking-wide transition-all active:scale-95 shadow-[0_0_15px_rgba(245,158,11,0.3)] mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating account...
                </>
              ) : (
                'Create account'
              )}
            </Button>
          </form>

          {/* Login Link */}
          <p className="mt-6 text-center text-slate-400 font-['Manrope']">
            Already have an account?{' '}
            <Link 
              to="/login" 
              data-testid="login-link"
              className="text-amber-500 hover:text-amber-400 transition-colors"
            >
              Sign in
            </Link>
          </p>

          {/* Passkey info */}
          <p className="mt-3 text-center text-slate-500 text-xs">
            After creating your account, you can add passkey (fingerprint/Face ID) in Settings → Security
          </p>
        </div>
      </div>

      {/* Right Side - Hero Image */}
      <div className="hidden lg:block relative">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1751380911352-c216693fb27b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHw0fHxhZXJpYWwlMjB2aWV3JTIwY29udGFpbmVyJTIwcG9ydCUyMG5pZ2h0JTIwbGlnaHRzfGVufDB8fHx8MTc2NTg2MTg5MXww&ixlib=rb-4.1.0&q=85)'
          }}
        />
        <div className="absolute inset-0 bg-slate-950/60" />
        <div className="absolute bottom-12 left-12 right-12">
          <blockquote className="text-white font-['Manrope']">
            <p className="text-xl md:text-2xl font-medium mb-4 leading-relaxed">
              "Empowering businesses and drivers across Jamaica."
            </p>
            <footer className="text-slate-400 text-sm">
              <span className="font-mono text-amber-500">LIANTIAO</span> — Enterprise Logistics Platform
            </footer>
          </blockquote>
        </div>
      </div>
    </div>
  );
};

export default Register;
